const pendingToolSubmissions = new WeakMap()
const pendingToolSubmissionRecords = new Set()
const pendingToolSubmissionsByFrame = new WeakMap()
const nativeToolSubmissionStorageKey = "nazoStep.pendingToolSubmit"
const nativeToolSubmissionTtlMs = 5 * 60 * 1000
const nativeToolOutcomes = new Set(["success", "no_result", "validation_error"])

function safePagePath(location = window.location) {
  const pathname = typeof location?.pathname === "string" ? location.pathname : "/"

  try {
    const parsedPath = new URL(pathname, "https://nazostep.invalid")
    return parsedPath.pathname.startsWith("/") ? parsedPath.pathname : "/"
  } catch (_error) {
    return pathname.split(/[?#]/, 1)[0] || "/"
  }
}

function safePageLocation(location = window.location) {
  const path = safePagePath(location)

  try {
    const origin = typeof location?.origin === "string" ? new URL(location.origin).origin : ""
    return origin && origin !== "null" ? `${origin}${path}` : path
  } catch (_error) {
    return path
  }
}

function safePageReferrer(referrer = typeof document === "undefined" ? "" : document.referrer) {
  if (typeof referrer !== "string" || referrer.trim() === "") return ""

  try {
    const parsedReferrer = new URL(referrer)
    if (!parsedReferrer.origin || parsedReferrer.origin === "null" || !["http:", "https:"].includes(parsedReferrer.protocol)) return ""

    const currentOrigin = typeof window !== "undefined" && typeof window.location?.origin === "string" ? window.location.origin : ""
    return parsedReferrer.origin === currentOrigin ? `${parsedReferrer.origin}${safePagePath(parsedReferrer)}` : parsedReferrer.origin
  } catch (_error) {
    return ""
  }
}

function analyticsPayload(parameters = {}) {
  return {
    ...parameters,
    page_location: safePageLocation(),
    page_path: safePagePath(),
    page_referrer: safePageReferrer()
  }
}

window.NazoStepAnalytics = {
  ...(window.NazoStepAnalytics || {}),
  safePageLocation,
  safePagePath,
  safePageReferrer
}

function toolPath(form) {
  try {
    const path = new URL(form.action, window.location.href).pathname
    return path.startsWith("/tools/") ? path : null
  } catch (_error) {
    return null
  }
}

function sendAnalyticsEvent(name, parameters) {
  if (typeof window.gtag !== "function") return

  window.gtag("event", name, analyticsPayload(parameters))
}

function sessionStorageOrNull() {
  try {
    return window.sessionStorage
  } catch (_error) {
    return null
  }
}

function trackNativeToolSubmit(event) {
  const form = event.target
  if (!(form instanceof HTMLFormElement) || form.getAttribute("data-turbo") !== "false") return

  const path = toolPath(form)
  const storage = sessionStorageOrNull()
  if (!path || !storage) return

  const createdAt = Date.now()
  const marker = {
    id: `${createdAt}-${Math.random().toString(36).slice(2)}`,
    tool_path: path,
    created_at: createdAt
  }

  try {
    storage.setItem(nativeToolSubmissionStorageKey, JSON.stringify(marker))
  } catch (_error) {
    // Storage can be disabled by the browser. Analytics must never break the submit.
  }
}

function sendNativeToolAnalytics(toolPathValue, outcome) {
  sendAnalyticsEvent("tool_submit", { tool_path: toolPathValue })
  sendAnalyticsEvent("tool_submit_result", {
    tool_path: toolPathValue,
    outcome
  })
}

function consumeNativeToolSubmit() {
  const storage = sessionStorageOrNull()
  if (!storage) return

  let marker
  try {
    const serializedMarker = storage.getItem(nativeToolSubmissionStorageKey)
    if (!serializedMarker) return

    storage.removeItem(nativeToolSubmissionStorageKey)
    marker = JSON.parse(serializedMarker)
  } catch (_error) {
    return
  }

  if (!marker || typeof marker.id !== "string" || typeof marker.tool_path !== "string" ||
      !Number.isFinite(marker.created_at)) return

  const age = Date.now() - marker.created_at
  if (age < 0 || age > nativeToolSubmissionTtlMs || marker.tool_path !== safePagePath()) return

  const outcome = document.querySelector("[data-analytics-tool-outcome]")?.getAttribute("data-analytics-tool-outcome")
  if (!nativeToolOutcomes.has(outcome)) return

  sendNativeToolAnalytics(marker.tool_path, outcome)
}

function trackToolSubmit(event) {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return

  const path = toolPath(form)
  if (!path || typeof window.gtag !== "function") return

  const formSubmission = event.detail?.formSubmission
  const frame = form.closest("turbo-frame")
  if (!formSubmission) return

  if (frame) discardPendingToolSubmissionsForFrame(frame)

  const record = { formSubmission, frame, toolPath: path }
  pendingToolSubmissions.set(formSubmission, record)
  pendingToolSubmissionRecords.add(record)

  if (frame) {
    const frameRecords = pendingToolSubmissionsByFrame.get(frame) || new Set()
    frameRecords.add(record)
    pendingToolSubmissionsByFrame.set(frame, frameRecords)
  }
}

function forgetToolSubmission(record) {
  pendingToolSubmissions.delete(record.formSubmission)
  pendingToolSubmissionRecords.delete(record)

  if (!record.frame) return

  const frameRecords = pendingToolSubmissionsByFrame.get(record.frame)
  if (!frameRecords) return

  frameRecords.delete(record)
  if (frameRecords.size === 0) pendingToolSubmissionsByFrame.delete(record.frame)
}

function frameForTarget(target) {
  if (!(target instanceof HTMLElement)) return null

  return target.matches("turbo-frame") ? target : target.closest("turbo-frame")
}

function discardPendingToolSubmission(event) {
  const frame = frameForTarget(event.target)
  if (!frame) return

  discardPendingToolSubmissionsForFrame(frame)
}

function discardPendingToolSubmissionsForFrame(frame) {
  const frameRecords = pendingToolSubmissionsByFrame.get(frame)
  if (!frameRecords) return

  Array.from(frameRecords).forEach(forgetToolSubmission)
}

function discardAllPendingToolSubmissions() {
  Array.from(pendingToolSubmissionRecords).forEach(forgetToolSubmission)
}

function trackToolResult(event) {
  const formSubmission = event.detail?.formSubmission
  const submission = formSubmission && pendingToolSubmissions.get(formSubmission)
  if (!submission) return

  const statusCode = event.detail?.fetchResponse?.statusCode
  if (typeof statusCode !== "number") {
    forgetToolSubmission(submission)
    return
  }

  if (statusCode >= 400 && statusCode < 500) {
    sendToolAnalytics(submission, "validation_error")
    return
  }

  if (statusCode < 200 || statusCode >= 300 || !submission.frame) {
    forgetToolSubmission(submission)
    return
  }

  submission.statusCode = statusCode
}

function trackToolFrameRender(event) {
  const frame = frameForTarget(event.target)
  if (!frame) return

  const frameRecords = pendingToolSubmissionsByFrame.get(frame)
  if (!frameRecords) return

  const submission = Array.from(frameRecords).find((record) => typeof record.statusCode === "number")
  if (!submission) return

  const outcome = frame.querySelector("[data-analytics-tool-result]") ? "success" : "no_result"
  sendToolAnalytics(submission, outcome)
}

function sendToolAnalytics(submission, outcome) {
  forgetToolSubmission(submission)

  // Emit both events only after the matching Turbo submission and frame have
  // completed. A newer submission removes the stale record before it can leak.
  sendAnalyticsEvent("tool_submit", {
    tool_path: submission.toolPath
  })
  sendAnalyticsEvent("tool_submit_result", {
    tool_path: submission.toolPath,
    outcome
  })
}

let lastTrackedPage
let configuredMeasurementId

function configureAnalytics(measurementId) {
  if (configuredMeasurementId === measurementId || typeof window.gtag !== "function") return

  configuredMeasurementId = measurementId
  window.gtag("config", measurementId, analyticsPayload({ send_page_view: false }))
}

function trackPageView() {
  const measurementId = window.nazoStepAnalyticsMeasurementId
  if (!measurementId || typeof window.gtag !== "function") return

  const pagePath = safePagePath()
  if (lastTrackedPage === pagePath) return

  configureAnalytics(measurementId)
  lastTrackedPage = pagePath
  sendAnalyticsEvent("page_view")
}

window.NazoStepAnalytics.trackPageView = trackPageView

// Turbo dispatches submit-start/end with the same FormSubmission object.
// Network failures emit fetch-request-error first, then a terminal submit-end
// without fetchResponse, so cleanup stays on this submission-owned path.
document.addEventListener("turbo:submit-start", trackToolSubmit)
document.addEventListener("turbo:submit-end", trackToolResult)
document.addEventListener("turbo:frame-render", trackToolFrameRender)
document.addEventListener("turbo:frame-missing", discardPendingToolSubmission)
document.addEventListener("turbo:before-cache", discardAllPendingToolSubmissions)
document.addEventListener("submit", trackNativeToolSubmit, true)
trackPageView()
consumeNativeToolSubmit()
document.addEventListener("turbo:load", trackPageView)
